import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {GripVertical, ChevronDown, ChevronRight, X, Plus, Info} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AIChatModeConfig } from '@/hooks/useAIChatModeConfig';
import {Textarea} from "@/components/ui/textarea.tsx";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { SortableFieldItem } from './SortableFieldItem';
import { FieldDictionary } from '@/types/fieldsDictionary';
import { VariableMapping } from '@/types/supabase';
import { useRef } from 'react';

interface SortableActionItemProps {
  action: NonNullable<AIChatModeConfig['offerGoals']>['goals'][number]['actions'][number];
  actionIndex: number;
  allActions: NonNullable<AIChatModeConfig['offerGoals']>['goals'][number]['actions'];
  onUpdateAction: (updates: Partial<NonNullable<AIChatModeConfig['offerGoals']>['goals'][number]['actions'][number]>) => void;
  onDeleteAction: () => void;
  fieldsDictionary: FieldDictionary[] | []
}

export const SortableActionItem: React.FC<SortableActionItemProps> = ({
  action,
  actionIndex,
  allActions,
  onUpdateAction,
  onDeleteAction,
  fieldsDictionary
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [fieldSearchMap, setFieldSearchMap] = useState<Record<number, string>>({});
  const searchInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const [pingPostProviders, setPingPostProviders] = useState<any[]>([]);
  const [isProvidersLoading, setIsProvidersLoading] = useState(false);
  const [providerSearch, setProviderSearch] = useState('');
  const [isProviderPopoverOpen, setIsProviderPopoverOpen] = useState(false);

  useEffect(() => {
    if (isExpanded && action.type === 'fields_set') {
      loadPingPostProviders();
    }
  }, [isExpanded, action.type]);

  const loadPingPostProviders = async () => {
    setIsProvidersLoading(true);
    try {
      const { data, error } = await supabase
        .from('pingpost_providers')
        .select('*')
        .eq('is_enabled', true);

      if (error) throw error;

      setPingPostProviders(data || []);
    } catch (error: any) {
      console.error('Error loading pingpost providers:', error);
    } finally {
      setIsProvidersLoading(false);
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: action.id });

  const style = {
    transform: CSS.Translate.toString(transform), // Use Translate instead of Transform to avoid scaling issues
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 999 : 'auto'
  };

  // Drag and drop sensors
  const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
  );

  // Provide stable drag IDs even when field.id is empty (newly added fields)
  const getFieldDragId = (field: { id: string }, index: number) =>
    field.id && field.id.trim() !== '' ? field.id : `field-${index}`;

  // Disable options already selected in other rows, keep current selection enabled
  const isFieldDisabled = (currentIndex: number, optionId: string) => {
    return (action.requiredFields || []).some((f, idx) => idx !== currentIndex && f.id?.trim() === optionId);
  };

  // Sort options so enabled go first
  const getSortedFieldsForSelect = (currentIndex: number) => {
    const term = (fieldSearchMap[currentIndex] || '').trim().toLowerCase();
    const currentId = action.requiredFields?.[currentIndex]?.id?.trim();

    const filtered = term
      ? fieldsDictionary.filter((fd: FieldDictionary) => fd.friendly_name.toLowerCase().includes(term))
      : fieldsDictionary;

    // Ensure current selection is present even if it doesn't match the search
    let list = [...filtered];
    if (currentId && !list.some(fd => fd.id === currentId)) {
      const existing = fieldsDictionary.find(fd => fd.id === currentId);
      list = existing ? [existing, ...list] : [{ id: currentId, friendly_name: currentId } as FieldDictionary, ...list];
    }

    return list.sort((a, b) => {
      // Current selection always at the top
      if (currentId) {
        if (a.id === currentId && b.id !== currentId) return -1;
        if (b.id === currentId && a.id !== currentId) return 1;
      }
      const aDisabled = isFieldDisabled(currentIndex, a.id);
      const bDisabled = isFieldDisabled(currentIndex, b.id);
      return Number(aDisabled) - Number(bDisabled);
    });
  };

  const handleRequiredFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const fields = action.requiredFields || [];
      const indexed = fields.map((f, idx) => ({ field: f, dragId: getFieldDragId(f, idx) }));
      const oldIndex = indexed.findIndex(item => item.dragId === active.id);
      const newIndex = indexed.findIndex(item => item.dragId === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedFields = arrayMove(indexed, oldIndex, newIndex).map(item => item.field);
        onUpdateAction({ requiredFields: reorderedFields });
      }
    }
  };

  // Helper function for field objects specifically with stable IDs
  const addFieldItem = () => {
    const currentArray = action.requiredFields as Array<{ id: string; }>;
    const newField = { id: '' };
    console.log(`➕ Adding new field to requiredFields:`, newField);
    onUpdateAction({ requiredFields: [...currentArray, newField] });
  };

  const removeFieldItem = (index: number) => {
    const currentArray = action.requiredFields as Array<{ id: string; }>;
    console.log(`➖ Removing field from requiredFields at index ${index}`);
    onUpdateAction({ requiredFields: currentArray.filter((_, i) => i !== index) });
  };

  const handleProviderToggle = (providerId: string) => {
    const currentProviders = action.triggersPingPost || [];
    const isSelected = currentProviders.some(p => p.id === providerId);

    if (isSelected) {
      onUpdateAction({
        triggersPingPost: currentProviders.filter(p => p.id !== providerId)
      });
    } else {
      onUpdateAction({
        triggersPingPost: [...currentProviders, { id: providerId }]
      });
    }
  };

  const filteredProviders = pingPostProviders.filter(provider =>
    provider.name.toLowerCase().includes(providerSearch.toLowerCase())
  );

  const selectedProviderIds = new Set((action.triggersPingPost || []).map(p => p.id));

  return (
    <div ref={setNodeRef} style={style} className="border rounded-lg bg-background">
      {/* Header */}
      <div 
        className="flex items-center gap-2 p-3 hover:bg-accent/50 transition-colors cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div
          {...attributes}
          {...listeners}
          className="flex items-center pt-1 cursor-grab active:cursor-grabbing"
          title="Drag to change action priority"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>

        <div className="flex items-center gap-3 flex-1">
          <div className="flex items-center justify-center w-20 h-8 rounded-md bg-primary/10 text-primary text-xs font-medium">
             Priority {action.priority + 1}
          </div>
          
          <div className="flex-1">
            <div className="font-medium text-sm">{action.name}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{action.description}</div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteAction();
              }}
            >
              <X className="h-4 w-4" />
            </Button>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Configuration */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-0 border-t bg-muted/30">
          <div className="mt-3 space-y-3">
          {action.type === 'yes_no_buttons' && (
            <>
              <div>
                <Label className="text-xs text-muted-foreground">Context / Instructions</Label>
                <Textarea
                    value={action.context || ''}
                    onChange={(e) => {
                      onUpdateAction({ context: e.target.value });
                    }}
                    placeholder="Tell the AI when and how to present this choice. E.g., 'Ask if they want to schedule a consultation'"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">'Yes' Button Text</Label>
                  <Input
                    value={action.yesButtonText || ''}
                    onChange={(e) => onUpdateAction({ yesButtonText: e.target.value })}
                    placeholder="Yes, I'm interested"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">'No' Button Text</Label>
                  <Input
                      value={action.noButtonText || ''}
                      onChange={(e) => onUpdateAction({ noButtonText: e.target.value })}
                      placeholder="No, not now"
                  />
                </div>
              </div>
            </>
          )}

          {action.type === 'button' && (
            <>
              <div>
                <Label className="text-xs text-muted-foreground">Context / Instructions</Label>
                <Textarea
                    value={action.context || ''}
                    onChange={(e) => {
                      onUpdateAction({ context: e.target.value });
                    }}
                    placeholder="Tell the AI how to introduce this button. E.g., 'Offer them to view our pricing page'"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Button Text</Label>
                  <Input
                    value={action.buttonText || ''}
                    onChange={(e) => onUpdateAction({ buttonText: e.target.value })}
                    placeholder="Schedule Appointment"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Button URL</Label>
                  <Input
                    value={action.buttonUrl || ''}
                    onChange={(e) => onUpdateAction({ buttonUrl: e.target.value })}
                    placeholder="https://example.com/schedule"
                  />
                </div>
              </div>
            </>
          )}

          {action.type === 'phone' && (
            <>
              <div>
                <Label className="text-xs text-muted-foreground">Context / Instructions</Label>
                <Textarea
                    value={action.context || ''}
                    onChange={(e) => {
                      onUpdateAction({ context: e.target.value });
                    }}
                    placeholder="Tell the AI how to present the phone option. E.g., 'Offer to connect them with a specialist by phone'"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Phone Number</Label>
                  <Input
                    value={action.phoneNumber || ''}
                    onChange={(e) => onUpdateAction({ phoneNumber: e.target.value })}
                    placeholder="1-800-555-0123"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Phone Text</Label>
                  <Input
                    value={action.phoneText || ''}
                    onChange={(e) => onUpdateAction({ phoneText: e.target.value })}
                    placeholder="Call us now to schedule"
                  />
                </div>
              </div>
            </>
          )}

            {action.type === 'fields_set' && (
                <>
                  {/*<div>*/}
                  {/*  <Label className="text-xs text-muted-foreground">Context / Instructions</Label>*/}
                  {/*  <Textarea*/}
                  {/*      value={action.context || ''}*/}
                  {/*      onChange={(e) => {*/}
                  {/*        onUpdateAction({ context: e.target.value });*/}
                  {/*      }}*/}
                  {/*      placeholder="Tell the AI how to present the phone option. E.g., 'Offer to connect them with a specialist by phone'"*/}
                  {/*  />*/}
                  {/*</div>*/}
                  <div className="space-y-3">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleRequiredFieldDragEnd}
                    >
                      <SortableContext
                          items={action.requiredFields.map((f, idx) => getFieldDragId(f, idx))}
                          strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2">
                          {action.requiredFields.map((field, index) => {
                            const fieldObj = field;
                            const fieldId = getFieldDragId(fieldObj, index);
                            return (
                                <SortableFieldItem key={fieldId} id={fieldId}>
                                  {(dragHandleProps) => (
                                      <div className="flex items-center gap-2 p-3 border rounded-lg bg-background">
                                        <div
                                            {...dragHandleProps}
                                            className="flex items-center cursor-grab active:cursor-grabbing"
                                            title="Drag to reorder field collection priority"
                                        >
                                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        <div className="flex items-center flex-1 space-y-1">
                                          <span className="text-xs text-muted-foreground mt-0.5 text-nowrap mr-3">Choose field</span>
                                          <Select
                                              value={fieldObj.id}
                                              onValueChange={(value) => {
                                                const newFields = [...action.requiredFields];
                                                newFields[index] = { ...field, id: value };
                                                onUpdateAction({ requiredFields: newFields });
                                              }}
                                              onOpenChange={(open) => {
                                                if (open) {
                                                  setFieldSearchMap(prev => ({ ...prev, [index]: '' }));
                                                  setTimeout(() => {
                                                    searchInputRefs.current[index]?.focus();
                                                  }, 0);
                                                }
                                              }}
                                          >
                                            <SelectTrigger>
                                              <SelectValue placeholder="Select field..." />
                                            </SelectTrigger>
                                              <SelectContent>
                                                <div className="px-2 pb-2">
                                                  <Input
                                                    placeholder="Search field..."
                                                    value={fieldSearchMap[index] || ''}
                                                    onChange={(e) => setFieldSearchMap(prev => ({ ...prev, [index]: e.target.value }))}
                                                    ref={(el) => { searchInputRefs.current[index] = el; }}
                                                    onKeyDown={(e) => {
                                                      // Prevent Radix Select typeahead from moving focus while typing in search
                                                      e.stopPropagation();
                                                    }}
                                                  />
                                                </div>
                                                {(() => {
                                                  const options = getSortedFieldsForSelect(index);
                                                  if (options.length === 0) {
                                                    return (
                                                      <SelectItem key="no-results" value="no-results" disabled>
                                                        No fields match
                                                      </SelectItem>
                                                    );
                                                  }

                                                  return options.map((fieldDict) => (
                                                    <SelectItem
                                                      key={fieldDict.id}
                                                      value={fieldDict.id}
                                                      disabled={isFieldDisabled(index, fieldDict.id)}
                                                    >
                                                      {fieldDict.friendly_name}
                                                    </SelectItem>
                                                  ));
                                                })()}
                                                {/* Preserve current value even if filtered out */}
                                                {action.requiredFields[index]?.id &&
                                                !getSortedFieldsForSelect(index).some(fd => fd.id === action.requiredFields[index]?.id) && (
                                                  <SelectItem
                                                    key={`current-${action.requiredFields[index]?.id}`}
                                                    value={action.requiredFields[index]?.id}
                                                    disabled={isFieldDisabled(index, action.requiredFields[index]?.id || '')}
                                                  >
                                                    {action.requiredFields[index]?.id}
                                                  </SelectItem>
                                                )}
                                              </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="flex items-center">
                                          <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => removeFieldItem(index)}
                                          >
                                            <X className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </div>
                                  )}
                                </SortableFieldItem>
                            );
                          })}
                        </div>
                      </SortableContext>
                    </DndContext>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addFieldItem()}
                        className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Required Field
                    </Button>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground mt-0.5 text-nowrap mr-3">Trigger Webhook Integrations</span>
                      <Popover open={isProviderPopoverOpen} onOpenChange={setIsProviderPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-between font-normal"
                            disabled={isProvidersLoading}
                          >
                            <span>
                              {isProvidersLoading
                                ? 'Loading integrations...'
                                : selectedProviderIds.size > 0
                                  ? `${selectedProviderIds.size} provider${selectedProviderIds.size > 1 ? 's' : ''} selected`
                                  : 'Select integrations to trigger'
                              }
                            </span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                        <div className="p-3">
                          <div className="space-y-2">
                            <Input
                              placeholder="Search integrations..."
                              value={providerSearch}
                              onChange={(e) => setProviderSearch(e.target.value)}
                              className="h-9"
                            />
                          </div>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {filteredProviders.length === 0 ? (
                            <div className="p-3 text-sm text-muted-foreground text-center">
                              {isProvidersLoading ? 'Loading...' : 'No integrationss found'}
                            </div>
                          ) : (
                            filteredProviders.map((provider) => {
                              const isSelected = selectedProviderIds.has(provider.id);
                              return (
                                <div
                                  key={provider.id}
                                  className="flex items-center justify-between p-3 hover:bg-accent/50 cursor-pointer border-b last:border-b-0"
                                  onClick={() => handleProviderToggle(provider.id)}
                                >
                                  <div className="flex items-center space-x-3">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => {}} // handled by onClick
                                      className="rounded border-gray-300"
                                    />
                                    <div>
                                      <div className="text-sm">{provider.name}</div>
                                      <div className="text-xs text-muted-foreground">{provider.url}</div>
                                    </div>
                                  </div>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <div className="max-w-xs">
                                          <div className="mb-1"><span className="text-muted-foreground">URL: </span>{provider.url}</div>
                                          <div className="text-sm">
                                            <div className="mb-1"><span className="text-muted-foreground">Variable Mappings: </span></div>
                                            {Array.isArray(provider.variable_mappings) && provider.variable_mappings.length > 0 ? (
                                              provider.variable_mappings.map((mapping: VariableMapping, idx: number) => (
                                                <div key={idx} className="text-xs">
                                                  {fieldsDictionary.find((field: FieldDictionary) => field.id === mapping.key_dictionary_id)?.key || mapping.key_dictionary_id} → {mapping.post_name}
                                                </div>
                                              ))
                                            ) : (
                                              <div className="text-xs text-muted-foreground">No mappings configured</div>
                                            )}
                                          </div>
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  </div>
                </>
              )}

          {action.type === 'continue' && (
            <p className="text-sm text-muted-foreground">
              No configuration needed. The conversation will flow naturally without a specific action.
            </p>
          )}
        </div>
      </div>
      )}
    </div>
  );
};
